import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { applicationAPI } from '../services/api';
import settingDefinitionsAPI from '../services/settingDefinitionsAPI';
import SAMLConfiguration from './SAMLConfiguration';
import CustomFieldsManager from './CustomFieldsManager';

const ApplicationDetails = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadApp = async () => {
    try {
      setLoading(true);
      const response = await applicationAPI.get(appId);
      setApp(response.application);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load application');
      console.error('Load app error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appId) {
      loadApp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  if (loading) return <div className="flex justify-center items-center h-64">Loading...</div>;
  if (!app) return <div className="p-6">Application not found</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button onClick={() => navigate('/applications')} className="mr-4 text-gray-600">← Back</button>
              <div className="flex items-center">
                {app.logo_url ? (
                  <img 
                    src={app.logo_url.startsWith('/') ? `${process.env.REACT_APP_API_URL || ''}${app.logo_url}` : app.logo_url}
                    alt={app.label}
                    className="w-12 h-12 rounded object-contain mr-4"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 bg-primary-light rounded flex items-center justify-center text-2xl mr-4">
                    {app.app_type === 'saml' ? '🔐' : app.app_type === 'bookmark' ? '🔖' : '🌐'}
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold">{app.label}</h1>
                  <p className="text-sm text-gray-500">{app.app_type} application</p>
                </div>
              </div>
            </div>
            <span className={`px-3 py-1 rounded text-sm ${app.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
              {app.status}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {['users', 'access', ...(app.app_type === 'saml' ? ['saml-config'] : []), 'configuration', 'custom-fields'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500'
                }`}
              >
                {tab === 'custom-fields' ? 'Custom Fields' : tab === 'saml-config' ? 'SAML Config' : tab === 'configuration' ? 'Configuration' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
            <button onClick={() => setError('')} className="float-right font-bold">×</button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
            <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'users' && <UsersTab appId={appId} app={app} onSuccess={setSuccess} onError={setError} />}
        {activeTab === 'access' && <AccessTab appId={appId} app={app} onSuccess={setSuccess} onError={setError} />}
        {activeTab === 'saml-config' && app.app_type === 'saml' && (
          <SAMLConfiguration
            appId={appId}
            onSuccess={(msg) => {
              setSuccess(msg);
              setTimeout(() => setSuccess(''), 3000);
            }}
            onError={(msg) => setError(msg)}
          />
        )}
        {activeTab === 'configuration' && (
          <ConfigurationTab
            app={app}
            appId={appId}
            onUpdate={loadApp}
            onDelete={() => navigate('/applications')}
            onSuccess={setSuccess}
            onError={setError}
          />
        )}
        {activeTab === 'custom-fields' && <CustomFieldsManager appId={appId} onSuccess={setSuccess} onError={setError} />}
      </div>

    </div>
  );
};

const UsersTab = ({ appId, app, onSuccess, onError }) => {
  const [effectiveUsers, setEffectiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await applicationAPI.getEffectiveUsers(appId);
      setEffectiveUsers(response.users || []);
    } catch (err) {
      onError(err.message || 'Failed to load users');
      console.error('Load data error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const filteredUsers = effectiveUsers.filter(u => 
    u.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.primary_role?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Effective Users ({effectiveUsers.length})</h3>
          <span className="text-gray-400 text-sm">
            Use the <span className="font-medium text-primary">Access</span> tab to manage role assignments
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Users who have access to this application through their assigned roles.
        </p>
        {effectiveUsers.length > 0 && (
          <input
            type="text"
            placeholder="Search users, emails, or roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
          />
        )}
      </div>
      
      {effectiveUsers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">👥</div>
          <h3 className="text-lg font-medium mb-2">No users have access</h3>
          <p className="text-gray-500 mb-4">Go to the Access tab to assign roles to this application.</p>
        </div>
      ) : (
        <div className="divide-y">
          {filteredUsers.map(user => (
            <div key={user.user_id} className="hover:bg-gray-50">
              <div 
                className="px-6 py-4 flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedUser(expandedUser === user.user_id ? null : user.user_id)}
              >
                <div className="flex items-center flex-1">
                  <div className="h-10 w-10 bg-primary-light rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary font-medium">
                      {user.email[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{user.display_name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-light text-indigo-800">
                    {user.primary_role?.name || 'No Role'}
                  </span>
                  {Object.keys(user.effective_settings).length > 0 && (
                    <span className="text-xs text-gray-500">
                      {Object.keys(user.effective_settings).length} setting(s)
                    </span>
                  )}
                  <svg 
                    className={`w-5 h-5 text-gray-400 transform transition-transform ${expandedUser === user.user_id ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              
              {expandedUser === user.user_id && (
                <div className="px-6 pb-4 bg-gray-50">
                  <div className="ml-13 pl-10 space-y-3">
                    {/* Roles */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Roles Granting Access</h4>
                      <div className="flex flex-wrap gap-2">
                        {user.all_roles.map(role => (
                          <span 
                            key={role.id} 
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              role.id === user.primary_role?.id 
                                ? 'bg-primary-light text-indigo-800' 
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {role.name}
                            {role.id === user.primary_role?.id && ' (primary)'}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Effective Settings */}
                    {Object.keys(user.effective_settings).length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Effective Custom Field Values</h4>
                        <div className="bg-white rounded border">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Field</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Value</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">From Role</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {Object.entries(user.effective_settings).map(([key, setting]) => (
                                <tr key={key}>
                                  <td className="px-3 py-2 font-medium">{key}</td>
                                  <td className="px-3 py-2">{setting.value}</td>
                                  <td className="px-3 py-2 text-gray-500">{setting.from_role}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {Object.keys(user.effective_settings).length === 0 && (
                      <p className="text-sm text-gray-500 italic">No custom field values configured for this user's roles.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {filteredUsers.length === 0 && searchTerm && (
            <div className="px-6 py-8 text-center text-gray-500">
              No users match "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AssignModal = ({ appId, users, onClose, onSuccess, onError }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');

  const handleAssign = async () => {
    if (!selectedUser) return onError('Select a user');
    
    try {
      await applicationAPI.assignUser(appId, selectedUser.id);
      onSuccess('User assigned successfully');
    } catch (err) {
      onError(err.message);
    }
  };

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between">
          <h3 className="text-lg font-medium">Assign User</h3>
          <button onClick={onClose} className="text-gray-400 text-2xl">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select User</label>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-2"
            />
            <div className="border rounded max-h-48 overflow-y-auto">
              {filtered.map(u => (
                <div
                  key={u.id}
                  onClick={() => setSelectedUser(u)}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                    selectedUser && selectedUser.id === u.id ? 'bg-primary-light border-l-4 border-primary' : ''
                  }`}
                >
                  <div className="font-medium">{u.firstname} {u.lastname}</div>
                  <div className="text-sm text-gray-500">{u.email}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end space-x-2">
          <button onClick={onClose} className="px-4 py-2 border rounded">Cancel</button>
          <button
            onClick={handleAssign}
            disabled={!selectedUser}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover disabled:bg-gray-400"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

const OIDCConfigTab = ({ appId, onSuccess, onError }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [newRedirectUri, setNewRedirectUri] = useState('');
  const [editConfig, setEditConfig] = useState({});
  const [customFieldDefs, setCustomFieldDefs] = useState([]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await applicationAPI.getOidcConfig(appId);
      setConfig(data);
      setEditConfig({
        redirect_uris: data.redirect_uris || [],
        require_pkce: data.require_pkce ?? true,
        require_consent: data.require_consent ?? false,
        allow_refresh_tokens: data.allow_refresh_tokens ?? true,
        access_token_lifetime: data.access_token_lifetime || 3600,
        refresh_token_lifetime: data.refresh_token_lifetime || 2592000,
      });
    } catch (err) {
      onError(err.error || 'Failed to load OIDC configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    settingDefinitionsAPI.getAppDefinitions(appId, true)
      .then(defs => setCustomFieldDefs(defs || []))
      .catch(() => {});
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateCredentials = async () => {
    if (!window.confirm('Generate OIDC credentials for this application?')) return;
    try {
      const data = await applicationAPI.generateOidcCredentials(appId);
      setNewSecret(data.client_secret);
      onSuccess('Credentials generated! Copy the client_secret now — it will not be shown again.');
      loadConfig();
    } catch (err) {
      onError(err.error || 'Failed to generate credentials');
    }
  };

  const handleRegenerateSecret = async () => {
    if (!window.confirm('Regenerate client secret? The old secret will be immediately invalidated.')) return;
    try {
      const data = await applicationAPI.regenerateOidcSecret(appId);
      setNewSecret(data.client_secret);
      onSuccess('New secret generated! Copy it now — it will not be shown again. Old secret is invalidated.');
    } catch (err) {
      onError(err.error || 'Failed to regenerate secret');
    }
  };

  const handleAddRedirectUri = () => {
    const uri = newRedirectUri.trim();
    if (!uri) return;
    if (editConfig.redirect_uris.includes(uri)) return;
    setEditConfig({ ...editConfig, redirect_uris: [...editConfig.redirect_uris, uri] });
    setNewRedirectUri('');
  };

  const handleRemoveRedirectUri = (uri) => {
    setEditConfig({ ...editConfig, redirect_uris: editConfig.redirect_uris.filter(u => u !== uri) });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await applicationAPI.updateOidcConfig(appId, editConfig);
      onSuccess('OIDC configuration saved');
      loadConfig();
    } catch (err) {
      onError(err.error || 'Failed to save OIDC configuration');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    onSuccess('Copied to clipboard');
  };

  if (loading) return <div className="p-6">Loading OIDC configuration...</div>;

  const discoveryUrl = `${window.location.origin}/.well-known/openid-configuration`;

  return (
    <div className="space-y-6">
      {/* Credentials Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Client Credentials</h3>
        
        {!config?.client_id ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">🔑</div>
            <h4 className="text-lg font-medium mb-2">No OIDC Credentials</h4>
            <p className="text-gray-500 mb-4">Generate a client_id and client_secret to enable OIDC login for this application.</p>
            <button onClick={handleGenerateCredentials} className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-hover">
              Generate Credentials
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border rounded font-mono text-sm">{config.client_id}</code>
                <button onClick={() => copyToClipboard(config.client_id)} className="px-3 py-2 text-sm border rounded hover:bg-gray-50">Copy</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
              {newSecret ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded font-mono text-sm break-all">{newSecret}</code>
                    <button onClick={() => copyToClipboard(newSecret)} className="px-3 py-2 text-sm border rounded hover:bg-gray-50">Copy</button>
                  </div>
                  <p className="text-xs text-yellow-700 font-medium">⚠️ Copy this secret now — it will not be shown again!</p>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="flex-1 px-3 py-2 bg-gray-50 border rounded text-sm text-gray-500">••••••••••••••••••••••••••••••••</span>
                  <button onClick={handleRegenerateSecret} className="px-3 py-2 text-sm border border-red-200 text-red-600 rounded hover:bg-red-50">Regenerate</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Reference */}
      {config?.client_id && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Quick Reference (for developers)</h3>
          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-100 space-y-1">
            <div><span className="text-gray-400">Discovery:</span> <span className="text-green-400">{discoveryUrl}</span></div>
            <div><span className="text-gray-400">Authorize:</span> <span className="text-green-400">{window.location.origin}/oauth/authorize</span></div>
            <div><span className="text-gray-400">Token:</span>     <span className="text-green-400">{window.location.origin}/oauth/token</span></div>
            <div><span className="text-gray-400">UserInfo:</span>  <span className="text-green-400">{window.location.origin}/oauth/userinfo</span></div>
            <div><span className="text-gray-400">Client ID:</span> <span className="text-blue-400">{config.client_id}</span></div>
          </div>
          <button onClick={() => copyToClipboard(discoveryUrl)} className="mt-2 text-sm text-primary hover:text-primary">
            Copy discovery URL
          </button>
        </div>
      )}

      {/* JWT Token Schema */}
      {config?.client_id && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">JWT Token Schema</h3>
          <p className="text-sm text-gray-500 mb-4">The structure of tokens and responses your application will receive.</p>

          <div className="space-y-4">
            {/* Access Token */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Access Token Claims</h4>
              <pre className="bg-gray-900 rounded-lg p-4 font-mono text-xs text-gray-100 overflow-x-auto">{JSON.stringify({
                sub: 42,
                aud: config.client_id,
                token_type: 'access',
                scope: 'openid profile email company_users offline_access',
                company_id: 2,
                company_name: 'Company Name',
                ...Object.fromEntries(customFieldDefs.filter(d => d.protocol_claim_name).map(d =>
                  [d.protocol_claim_name, d.source_type === 'user_field' ? '(user data)' : d.source_type === 'static_value' ? (d.static_value || 'value') : (d.field_options?.[0]?.value || 'value')]
                )),
                ...(customFieldDefs.filter(d => d.source_type === 'custom_field' && !d.protocol_claim_name).length > 0 ? {
                  custom_fields: Object.fromEntries(customFieldDefs.filter(d => d.source_type === 'custom_field' && !d.protocol_claim_name).map(d =>
                    [d.setting_key, d.field_type === 'boolean' ? 'true' : (d.field_options?.[0]?.value || 'value')]
                  ))
                } : {}),
                exp: 1749091200,
                iat: 1749087600
              }, null, 2)}</pre>
            </div>

            {/* ID Token */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">ID Token Claims <span className="text-gray-400 font-normal">(requires openid scope)</span></h4>
              <pre className="bg-gray-900 rounded-lg p-4 font-mono text-xs text-gray-100 overflow-x-auto">{JSON.stringify({
                sub: '42',
                iss: window.location.origin,
                aud: config.client_id,
                token_type: 'id_token',
                email: 'user@example.com',
                given_name: 'Jane',
                family_name: 'Doe',
                name: 'Jane Doe',
                company_id: 2,
                company_name: 'Company Name',
                ...Object.fromEntries(customFieldDefs.filter(d => d.protocol_claim_name).map(d =>
                  [d.protocol_claim_name, d.source_type === 'user_field' ? '(user data)' : d.source_type === 'static_value' ? (d.static_value || 'value') : (d.field_options?.[0]?.value || 'value')]
                )),
                ...(customFieldDefs.filter(d => d.source_type === 'custom_field' && !d.protocol_claim_name).length > 0 ? {
                  custom_fields: Object.fromEntries(customFieldDefs.filter(d => d.source_type === 'custom_field' && !d.protocol_claim_name).map(d =>
                    [d.setting_key, d.field_type === 'boolean' ? 'true' : (d.field_options?.[0]?.value || 'value')]
                  ))
                } : {}),
                exp: 1749091200,
                iat: 1749087600
              }, null, 2)}</pre>
            </div>

            {/* UserInfo */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">UserInfo Response <span className="text-gray-400 font-normal">(GET /oauth/userinfo)</span></h4>
              <pre className="bg-gray-900 rounded-lg p-4 font-mono text-xs text-gray-100 overflow-x-auto">{JSON.stringify({
                sub: '42',
                email: 'user@example.com',
                email_verified: true,
                given_name: 'Jane',
                family_name: 'Doe',
                name: 'Jane Doe',
                company_id: 2,
                company_name: 'Company Name',
                can_manage_users: true,
                ...Object.fromEntries(customFieldDefs.filter(d => d.protocol_claim_name).map(d =>
                  [d.protocol_claim_name, d.source_type === 'user_field' ? '(user data)' : d.source_type === 'static_value' ? (d.static_value || 'value') : (d.field_options?.[0]?.value || 'value')]
                )),
                ...(customFieldDefs.filter(d => d.source_type === 'custom_field' && !d.protocol_claim_name).length > 0 ? {
                  custom_fields: Object.fromEntries(customFieldDefs.filter(d => d.source_type === 'custom_field' && !d.protocol_claim_name).map(d =>
                    [d.setting_key, d.field_type === 'boolean' ? 'true' : (d.field_options?.[0]?.value || 'value')]
                  ))
                } : {})
              }, null, 2)}</pre>
            </div>

            {/* Custom Fields Legend */}
            {customFieldDefs.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Custom Fields Reference</h4>
                <p className="text-xs text-gray-500 mb-3">Values are resolved from the user's highest-priority role. All values are strings.</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs border-b">
                      <th className="pb-2 pr-4">Key</th>
                      <th className="pb-2 pr-4">Label</th>
                      <th className="pb-2 pr-4">Type</th>
                      <th className="pb-2">Possible Values</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customFieldDefs.map(d => (
                      <tr key={d.id} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs">{d.setting_key}</td>
                        <td className="py-2 pr-4">{d.setting_label}</td>
                        <td className="py-2 pr-4 text-gray-500">{d.field_type}</td>
                        <td className="py-2 text-gray-500 text-xs">
                          {d.field_type === 'boolean' ? '"true", "false"' :
                           d.field_options ? d.field_options.map(o => `"${o.value}"`).join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Redirect URIs */}
      {config?.client_id && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-2">Redirect URIs</h3>
          <p className="text-sm text-gray-500 mb-4">Allowed callback URLs. The app must redirect to one of these after authentication.</p>
          
          <div className="space-y-2 mb-4">
            {editConfig.redirect_uris.map((uri, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border rounded text-sm">{uri}</code>
                <button onClick={() => handleRemoveRedirectUri(uri)} className="text-red-500 hover:text-red-700 px-2">✕</button>
              </div>
            ))}
            {editConfig.redirect_uris.length === 0 && (
              <p className="text-sm text-gray-400 italic">No redirect URIs configured</p>
            )}
          </div>
          
          <div className="flex gap-2">
            <input
              type="url"
              value={newRedirectUri}
              onChange={(e) => setNewRedirectUri(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRedirectUri())}
              placeholder="https://yourapp.com/callback"
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
            />
            <button onClick={handleAddRedirectUri} className="px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200">Add</button>
          </div>
        </div>
      )}

      {/* Settings */}
      {config?.client_id && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Security & Token Settings</h3>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={editConfig.require_pkce}
                onChange={(e) => setEditConfig({ ...editConfig, require_pkce: e.target.checked })}
                className="rounded" />
              <div>
                <div className="font-medium text-sm">Require PKCE</div>
                <div className="text-xs text-gray-500">Proof Key for Code Exchange — recommended for all apps</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={editConfig.require_consent}
                onChange={(e) => setEditConfig({ ...editConfig, require_consent: e.target.checked })}
                className="rounded" />
              <div>
                <div className="font-medium text-sm">Require User Consent</div>
                <div className="text-xs text-gray-500">Show a consent screen before redirecting back to the app</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={editConfig.allow_refresh_tokens}
                onChange={(e) => setEditConfig({ ...editConfig, allow_refresh_tokens: e.target.checked })}
                className="rounded" />
              <div>
                <div className="font-medium text-sm">Allow Refresh Tokens</div>
                <div className="text-xs text-gray-500">Issue refresh tokens when `offline_access` scope is requested</div>
              </div>
            </label>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token Lifetime (seconds)</label>
                <input type="number" value={editConfig.access_token_lifetime}
                  onChange={(e) => setEditConfig({ ...editConfig, access_token_lifetime: parseInt(e.target.value) || 3600 })}
                  className="w-full px-3 py-2 border rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">{Math.round(editConfig.access_token_lifetime / 60)} min</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Token Lifetime (seconds)</label>
                <input type="number" value={editConfig.refresh_token_lifetime}
                  onChange={(e) => setEditConfig({ ...editConfig, refresh_token_lifetime: parseInt(e.target.value) || 2592000 })}
                  className="w-full px-3 py-2 border rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">{Math.round(editConfig.refresh_token_lifetime / 86400)} days</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2 bg-primary text-white rounded hover:bg-primary-hover disabled:bg-gray-400">
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsTab = ({ app, onUpdate, onDelete, onSuccess, onError }) => {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: app.name || '',
    label: app.label || '',
    description: app.description || '',
    sign_on_url: app.sign_on_url || '',
    status: app.status || 'active',
    app_type: app.app_type || 'web'
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      onError('Invalid file type. Please upload PNG, JPG, GIF, WebP, or SVG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onError('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('logo', file);

      const response = await applicationAPI.uploadLogo(app.id, formData);
      onSuccess('Logo uploaded successfully');
      onUpdate();
    } catch (err) {
      onError(err.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to remove the logo?')) return;
    try {
      await applicationAPI.deleteLogo(app.id);
      onSuccess('Logo removed');
      onUpdate();
    } catch (err) {
      onError(err.message || 'Failed to remove logo');
    }
  };

  const handleSave = async () => {
    try {
      await applicationAPI.update(app.id, formData);
      onSuccess('Application settings updated successfully');
      setEditing(false);
      onUpdate();
    } catch (err) {
      onError(err.message || 'Failed to update application');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${app.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await applicationAPI.delete(app.id);
      onSuccess('Application deleted successfully');
      onDelete();
    } catch (err) {
      onError(err.message || 'Failed to delete application');
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Basic Information</h3>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="text-primary hover:text-primary text-sm font-medium"
            >
              Edit
            </button>
          ) : (
            <div className="space-x-2">
              <button
                onClick={() => setEditing(false)}
                className="text-gray-600 hover:text-gray-700 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-hover text-sm"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Application Name</label>
            {editing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-sm text-gray-900">{app.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Label</label>
            {editing ? (
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-sm text-gray-900">{app.label || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            {editing ? (
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-sm text-gray-900">{app.description || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sign-On URL</label>
            {editing ? (
              <input
                type="url"
                value={formData.sign_on_url}
                onChange={(e) => setFormData({ ...formData, sign_on_url: e.target.value })}
                placeholder="https://tickets.backups.net.au"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-sm text-gray-900">{app.sign_on_url || '-'}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">The URL where users access this application</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Application Logo</label>
            <div className="flex items-start gap-4">
              {/* Logo Preview */}
              <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {app.logo_url ? (
                  <img
                    src={app.logo_url.startsWith('/') ? `${process.env.REACT_APP_API_URL || ''}${app.logo_url}` : app.logo_url}
                    alt="App logo"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              {/* Upload Controls */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover text-sm font-medium inline-flex items-center">
                    {uploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload Logo
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                  {app.logo_url && (
                    <button
                      onClick={handleLogoDelete}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF, WebP, or SVG. Max 5MB. Will be resized to 256×256.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            {editing ? (
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            ) : (
              <span className={`inline-flex px-2 py-1 text-xs rounded ${
                app.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {app.status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Application Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Application Information</h3>
        <dl className="grid grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Application Type</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {editing ? (
                <select
                  value={formData.app_type}
                  onChange={(e) => setFormData({ ...formData, app_type: e.target.value })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="web">WEB (OIDC)</option>
                  <option value="spa">SPA (OIDC)</option>
                  <option value="native">NATIVE (OIDC)</option>
                  <option value="service">SERVICE (OIDC)</option>
                  <option value="saml">SAML</option>
                  <option value="bookmark">BOOKMARK</option>
                </select>
              ) : (
                <span>{app.app_type?.toUpperCase()}</span>
              )}
            </dd>
          </div>
          {app.client_id && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Client ID</dt>
              <dd className="mt-1 text-sm font-mono bg-gray-50 p-2 rounded">{app.client_id}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">Created</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {app.updated_at ? new Date(app.updated_at).toLocaleDateString() : '-'}
            </dd>
          </div>
          {app.owner && (
            <div className="col-span-2">
              <dt className="text-sm font-medium text-gray-500">Owner</dt>
              <dd className="mt-1 text-sm text-gray-900">{app.owner.email || app.owner.username}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-lg shadow p-6 border-2 border-red-200">
        <h3 className="text-lg font-medium text-red-600 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">
          Once you delete an application, there is no going back. All user assignments, roles, and configurations will be permanently deleted.
        </p>
        <button
          onClick={handleDelete}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Delete Application
        </button>
      </div>
    </div>
  );
};

const ConfigurationTab = ({ app, appId, onUpdate, onDelete, onSuccess, onError }) => {
  const isOidc = ['web', 'spa', 'native', 'service'].includes(app.app_type);

  return (
    <div className="space-y-8">
      <SettingsTab app={app} onUpdate={onUpdate} onDelete={onDelete} onSuccess={onSuccess} onError={onError} />
      {isOidc && <OIDCConfigTab appId={appId} onSuccess={onSuccess} onError={onError} />}
    </div>
  );
};

const AccessTab = ({ appId, app, onSuccess, onError }) => {
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [expandedRole, setExpandedRole] = useState(null);
  const [roleSettings, setRoleSettings] = useState({});
  const [editingSettings, setEditingSettings] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const rolesRes = await applicationAPI.getRoles(appId);
      setRoles(rolesRes.roles || []);
    } catch (err) {
      onError(err.message || 'Failed to load role data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllRoles = async () => {
    try {
      const { roleAPI } = await import('../services/api');
      const res = await roleAPI.getRoles(true, false);
      setAllRoles(res.roles || []);
    } catch (err) {
      console.error('Failed to load all roles:', err);
    }
  };

  useEffect(() => {
    if (appId) { loadData(); loadAllRoles(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const handleAddRole = async () => {
    if (!selectedRoleId) return;
    try {
      setSaving(true);
      const currentIds = roles.map(r => r.id);
      await applicationAPI.setRoles(appId, [...currentIds, parseInt(selectedRoleId)]);
      await loadData();
      setSelectedRoleId('');
      onSuccess('Role added successfully');
    } catch (err) {
      onError(err.message || 'Failed to add role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      setSaving(true);
      const newIds = roles.filter(r => r.id !== roleId).map(r => r.id);
      await applicationAPI.setRoles(appId, newIds);
      await loadData();
      setExpandedRole(null);
      onSuccess('Role removed successfully');
    } catch (err) {
      onError(err.message || 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const handleExpandRole = async (roleId) => {
    if (expandedRole === roleId) {
      setExpandedRole(null);
      return;
    }
    setExpandedRole(roleId);
    try {
      const res = await applicationAPI.getRoleSettings(appId, roleId);
      setRoleSettings(res.settings || {});
      setEditingSettings(false);
    } catch (err) {
      console.error('Failed to load role settings:', err);
    }
  };

  const handleSaveSettings = async (roleId) => {
    try {
      setSaving(true);
      await applicationAPI.updateRoleSettings(appId, roleId, roleSettings);
      setEditingSettings(false);
      onSuccess('Settings saved');
    } catch (err) {
      onError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addSetting = () => {
    setRoleSettings(prev => ({ ...prev, '': '' }));
    setEditingSettings(true);
  };

  const removeSetting = (key) => {
    const updated = { ...roleSettings };
    delete updated[key];
    setRoleSettings(updated);
    setEditingSettings(true);
  };

  const updateSettingKey = (oldKey, newKey) => {
    const updated = {};
    for (const [k, v] of Object.entries(roleSettings)) {
      updated[k === oldKey ? newKey : k] = v;
    }
    setRoleSettings(updated);
    setEditingSettings(true);
  };

  const updateSettingValue = (key, value) => {
    setRoleSettings(prev => ({ ...prev, [key]: value }));
    setEditingSettings(true);
  };

  const availableRoles = allRoles.filter(r => !roles.find(ar => ar.id === r.id));

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium">Role Access</h3>
        <p className="text-sm text-gray-500 mt-1">
          Assign roles to grant users access to this application. Users with these roles will see this app on their dashboard.
        </p>
      </div>

      <div className="px-6 py-4">
        {/* Add role section */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b">
          <select
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-sm"
            disabled={saving || availableRoles.length === 0}
          >
            <option value="">{availableRoles.length === 0 ? 'All roles assigned' : 'Select a role to add...'}</option>
            {availableRoles.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <button
            onClick={handleAddRole}
            disabled={!selectedRoleId || saving}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Role'}
          </button>
        </div>

        {/* Role list */}
        {roles.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">🔑</div>
            <p className="text-gray-500">No roles assigned yet. Add a role above to grant access.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {roles.map(role => (
              <div key={role.id} className="border rounded-lg">
                <div className="flex items-center justify-between px-4 py-3">
                  <button
                    onClick={() => handleExpandRole(role.id)}
                    className="flex-1 text-left flex items-center gap-3"
                  >
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{role.name}</div>
                      {role.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{role.description}</div>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {role.user_count || 0} users
                    </span>
                    {role.product && (
                      <span className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full border">
                        {role.product}
                      </span>
                    )}
                    <button
                      onClick={() => handleRemoveRole(role.id)}
                      className="text-red-500 hover:text-red-700 text-xs font-medium"
                      disabled={saving}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Expanded settings */}
                {expandedRole === role.id && (
                  <div className="border-t px-4 py-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Default Settings</h4>
                      <button
                        onClick={addSetting}
                        className="text-xs text-primary hover:text-primary font-medium"
                      >
                        + Add Setting
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">
                      These values are sent as SAML attributes or OIDC claims when a user with this role logs in to this app.
                    </p>

                    {Object.keys(roleSettings).length === 0 && (
                      <p className="text-xs text-gray-400 italic">No settings configured for this role.</p>
                    )}

                    {Object.entries(roleSettings).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={key}
                          onChange={(e) => updateSettingKey(key, e.target.value)}
                          placeholder="Key"
                          className="flex-1 px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-primary"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateSettingValue(key, e.target.value)}
                          placeholder="Value"
                          className="flex-1 px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-primary"
                        />
                        <button
                          onClick={() => removeSetting(key)}
                          className="text-red-400 hover:text-red-600 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {editingSettings && (
                      <button
                        onClick={() => handleSaveSettings(role.id)}
                        disabled={saving}
                        className="mt-3 px-4 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary-hover disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save Settings'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationDetails;
